import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MonitorPlay, MailCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/apiClient";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030305] px-6">
      <motion.div
        {...fadeUp}
        className="w-full max-w-md bg-[#0A0A0F] border border-white/10 rounded-sm p-8 md:p-10"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <MonitorPlay className="w-6 h-6 text-[#FF2A2A]" strokeWidth={2.5} />
          <span className="font-display text-xl tracking-tighter uppercase">QTV<span className="text-[#FF2A2A]">Network</span></span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <MailCheck className="w-12 h-12 text-[#FF2A2A] mx-auto mb-4" />
            <h1 className="font-display text-3xl uppercase tracking-tight mb-2">Check your email</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">
              If an account exists for <span className="text-white">{email}</span>, we've sent a link to reset your password. It expires in 1 hour.
            </p>
            <Link to="/login" data-testid="back-to-login-link" className="inline-block mt-8 text-white hover:text-[#FF2A2A] transition-colors text-sm">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl uppercase tracking-tight mb-1">Forgot password</h1>
            <p className="text-sm text-zinc-500 mb-8">Enter your email and we'll send you a reset link.</p>

            {error && (
              <div data-testid="forgot-error" className="mb-4 text-sm text-[#FF4D4D] bg-[#FF2A2A]/10 border border-[#FF2A2A]/30 px-4 py-2.5 rounded-sm">
                {error}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs tracking-[0.15em] uppercase text-zinc-500">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] transition"
                />
              </div>
              <button
                data-testid="forgot-submit-button"
                disabled={busy}
                className="w-full bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white py-3 rounded-sm font-semibold transition-colors"
              >
                {busy ? "Sending..." : "Send reset link"}
              </button>
            </form>

            <p className="mt-6 text-sm text-zinc-500 text-center">
              Remembered it?{" "}
              <Link to="/login" data-testid="go-login-link" className="text-white hover:text-[#FF2A2A] transition-colors">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
